import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { RestaurantTracker } from '../../../../services/admin/clientAnalyticsService';
import { lensEuros, costPctRatio, type Lens } from './trackerUtils';
import { useChartTheme } from '../../../../utils/chartTheme';

interface RestaurantTrendChartProps {
  restaurant: RestaurantTracker;
  lens: Lens;
  ccStartDate: string | null;
}

/** Short month label, e.g. "2025-10" → "Oct '25". */
function monthLabel(month: string): string {
  const [year, m] = month.split('-').map(Number);
  const name = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1];
  return `${name} '${String(year).slice(2)}`;
}

/** YYYY-MM of a CC start date, for placing the reference line. */
function startMonthKey(ccStartDate: string | null): string | null {
  if (!ccStartDate) return null;
  return ccStartDate.slice(0, 7);
}

export const RestaurantTrendChart = ({ restaurant, lens, ccStartDate }: RestaurantTrendChartProps) => {
  const theme = useChartTheme();
  const data = restaurant.months.map((b) => {
    const ratio = costPctRatio(lensEuros(b, lens));
    return {
      month: b.month,
      label: monthLabel(b.month),
      costPct: ratio === null ? null : Number((ratio * 100).toFixed(1)),
    };
  });

  const startMonth = startMonthKey(ccStartDate);
  const refLabel = startMonth ? monthLabel(startMonth) : null;
  const hasAnyData = data.some((d) => d.costPct !== null);

  if (!hasAnyData) {
    return (
      <div className="text-sm text-gray-400 dark:text-gray-500 py-4 px-2">
        No cost% data in range for this restaurant.
      </div>
    );
  }

  return (
    <div className="w-full h-56 py-2">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
          <XAxis dataKey="label" tick={{ fill: theme.axisTick, fontSize: 11 }} interval="preserveStartEnd" />
          <YAxis
            tick={{ fill: theme.axisTick, fontSize: 11 }}
            tickFormatter={(v: number) => `${v}%`}
            width={44}
            domain={[0, 'auto']}
          />
          <Tooltip
            formatter={(value) => [value == null ? '—' : `${value}%`, 'Cost %']}
            contentStyle={{ fontSize: 12, backgroundColor: theme.tooltipBg, borderColor: theme.tooltipBorder, color: theme.tooltipText }}
          />
          {refLabel && (
            <ReferenceLine
              x={refLabel}
              stroke="#a855f7"
              strokeDasharray="4 4"
              label={{ value: 'CC start', position: 'top', fontSize: 10, fill: '#a855f7' }}
            />
          )}
          <Line
            type="monotone"
            dataKey="costPct"
            stroke="#7c3aed"
            strokeWidth={2}
            dot={{ r: 2 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
